/**
 * A centralized, type-safe logger for the application.
 * It controls log output based on the environment (production/development)
 * and a `debugMode` flag that can be set in localStorage for admin debugging.
 */

// A function to check if logs should be displayed.
const shouldLog = (): boolean => {
  // Use `import.meta.env.MODE` for client-side code, which is set by Vite.
  // It will be 'production' on production builds, and 'development' otherwise.
  const isDevelopment = import.meta.env.MODE !== 'production';

  // A special flag that admins can enable in their browser console for live debugging
  // by executing: localStorage.setItem('debugMode', 'true')
  const isDebugMode = typeof window !== 'undefined' && window.localStorage.getItem('debugMode') === 'true';

  return isDevelopment || isDebugMode;
};

const Logger = {
  /**
   * Logs a debug message. Only visible in development or when debugMode is enabled.
   * @param {...any[]} args - Data to log.
   */
  debug: (...args: any[]): void => {
    if (shouldLog()) {
      console.log('[DEBUG]', ...args);
    }
  },

  /**
   * Logs an informational message. Only visible in development or when debugMode is enabled.
   * @param {...any[]} args - Data to log.
   */
  info: (...args: any[]): void => {
    if (shouldLog()) {
      console.info('[INFO]', ...args);
    }
  },

  /**
   * Logs a warning message. Only visible in development or when debugMode is enabled.
   * @param {...any[]} args - Data to log.
   */
  warn: (...args: any[]): void => {
    if (shouldLog()) {
      console.warn('[WARN]', ...args);
    }
  },

  /**
   * Logs an error message. Always visible in all environments for critical monitoring.
   * @param {...any[]} args - Data to log.
   */
  error: (...args: any[]): void => {
    console.error('[ERROR]', ...args);
  },
};

export default Logger;